import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { hostname } from "../providers/api";
import { carbonRequest } from "./corporateCarbon/api";
import { carbonFileSize, reportingRevisionStatusLabel } from './corporateCarbon/workflow';
import { ReportingRevisionFilter } from './corporateCarbon/ReportingRevisionFilter';
import { triggerBlobDownload } from "./corporateCarbon/downloadFilename";
import {
  CarbonAdminPage,
  CarbonConfirmDialog,
  CarbonDataTable,
  CarbonFeedback,
  CarbonFileInput,
  CarbonForm,
  CarbonSection,
  CarbonSelectInput,
  CarbonStatus,
  CarbonTableRow,
  CarbonTextInput,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonResourcePath, friendlyCarbonError } from "./corporateCarbon/workflow";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 560,
    [theme.breakpoints.down("xs")]: { minWidth: 0 },
  },
  metadata: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  metadataLabel: { display: "block", marginBottom: theme.spacing(0.5) },
  filename: { fontWeight: theme.typography.fontWeightMedium },
  tableActions: {
    display: "flex",
    flexWrap: "nowrap",
    gap: 0,
  },
  danger: { color: theme.palette.error.main },
  historyTable: { minWidth: 480 },
}));

const entityTypes = [
  ["CarbonActivity", "Activity"],
  ["CarbonSite", "Site"],
  ["CarbonInventoryRevision", "Inventory revision"],
];

const replacementScanMessage = (entry) => {
  if (entry.scanErrorCode === "CARBON_EVIDENCE_REPLACEMENT_TARGET_CHANGED")
    return "The selected draft changed before replacement completed. No evidence links were changed. This replacement was rejected; the original document remains usable. Contact support if you need to recover the uploaded file.";
  if (entry.lifecycleStatus === "rejected")
    return "This replacement did not pass processing. The original document remains usable with its links.";
  return null;
};

const EvidenceLinkTarget = React.memo(({
  entityGeneration,
  entityId,
  entityType,
  setEntityId,
  setEntityType,
}) => (
  <CarbonForm aria-label="Evidence link target" onSubmit={(event) => event.preventDefault()}>
    <CarbonSelectInput
      choices={entityTypes.map(([id, name]) => ({ id, name }))}
      emptyText={null}
      label="Link entity type"
      name="linkEntityType"
      value={entityType}
      onChange={(event) => {
        entityGeneration.current += 1;
        setEntityType(event.target.value);
      }}
    />
    <CarbonTextInput
      label="Link entity ID"
      name="linkEntityId"
      value={entityId}
      onChange={(event) => {
        entityGeneration.current += 1;
        setEntityId(event.target.value);
      }}
    />
  </CarbonForm>
));

const multipartRequest = async (token, path, files, fieldName = "file", fields = {}) => {
  const body = new FormData();
  const selectedFiles = Array.isArray(files) ? files : [files];
  selectedFiles.forEach((file) => body.append(fieldName, file));
  Object.entries(fields).forEach(([name, value]) => body.append(name, value));
  const response = await fetch(`${hostname}/${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(
      payload.message || payload.code || "Evidence upload failed.",
    );
  return payload;
};

export const CarbonEvidencePage = ({ accessToken, history: routeHistory }) => {
  const classes = useStyles();
  const tokenOps = useTokenOps();
  const token = accessToken || tokenOps.account?.accessToken;
  const generation = useRef(0);
  const entityGeneration = useRef(0);
  const historyGeneration = useRef(0);
  const uploadGeneration = useRef(0);
  const replacementGenerations = useRef({});
  const documentGeneration = useRef(0);
  const selectedHistoryIdRef = useRef(null);
  const tokenRef = useRef(token);
  const pendingRef = useRef(new Set());
  const [documents, setDocuments] = useState([]);
  const [category, setCategory] = useState('');
  const [filterRevisionId, setFilterRevisionId] = useState('');
  const [filterInventoryId, setFilterInventoryId] = useState('');
  useEffect(() => { setFilterRevisionId(''); setFilterInventoryId(''); }, [token]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pending, setPending] = useState([]);
  const [files, setFiles] = useState([]);
  const [replacementFiles, setReplacementFiles] = useState({});
  const [replacementTargets, setReplacementTargets] = useState({});
  const [replacementRevisionIds, setReplacementRevisionIds] = useState({});
  const [entityType, setEntityType] = useState("CarbonActivity");
  const [entityId, setEntityId] = useState("");
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [authorityReady, setAuthorityReady] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [managedDocumentId, setManagedDocumentId] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  tokenRef.current = token;
  selectedHistoryIdRef.current = selectedHistoryId;
  const markPending = (key, active) => {
    if (active) pendingRef.current.add(key);
    else pendingRef.current.delete(key);
    setPending(Array.from(pendingRef.current));
  };

  const load = async (
    currentGeneration = generation.current,
    targetPage = 1,
  ) => {
    const result = await carbonRequest(
      token,
      `corporate-carbon/evidence?page=${targetPage}&perPage=50&includeArchived=true${category ? `&category=${category}` : ''}${filterRevisionId ? `&revisionId=${filterRevisionId}` : ''}${filterInventoryId ? `&inventoryId=${filterInventoryId}` : ''}`,
    );
    if (currentGeneration === generation.current) {
      if (targetPage === 1) {
        historyGeneration.current += 1;
        setHistory([]);
        setSelectedHistoryId(null);
      }
      setDocuments((current) =>
        targetPage === 1 ? result.data : [...current, ...result.data],
      );
      documentGeneration.current += 1;
      setTotal(result.total);
      setPage(targetPage);
      setAuthorityReady(true);
    }
  };

  useEffect(() => {
    const currentGeneration = ++generation.current;
    pendingRef.current.clear();
    entityGeneration.current += 1;
    historyGeneration.current += 1;
    uploadGeneration.current += 1;
    replacementGenerations.current = {};
    documentGeneration.current += 1;
    setPending([]);
    setDocuments([]);
    setHistory([]);
    setSelectedHistoryId(null);
    setFiles([]);
    setReplacementFiles({});
    setReplacementTargets({});
    setReplacementRevisionIds({});
    setEntityType("CarbonActivity");
    setEntityId("");
    setTotal(0);
    setPage(1);
    setAuthorityReady(false);
    setHistoryTotal(0);
    setHistoryPage(1);
    setUploadOpen(false);
    setManagedDocumentId(null);
    setArchiveTarget(null);
    setError("");
    setLoading(true);
    if (!token) {
      setError("An active account token is required to load evidence.");
      setLoading(false);
      return undefined;
    }
    load(currentGeneration)
      .catch((requestError) => {
        if (currentGeneration === generation.current)
          setError(requestError.message);
      })
      .finally(() => {
        if (currentGeneration === generation.current) setLoading(false);
      });
    return () => {
      if (currentGeneration === generation.current) generation.current += 1;
    };
    // load is intentionally scoped to the active token generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, category, filterRevisionId, filterInventoryId]);

  const guarded = async (key, action, after, isCurrent = () => true) => {
    if (pendingRef.current.has(key)) return;
    const currentGeneration = generation.current;
    const currentToken = token;
    markPending(key, true);
    setError("");
    try {
      const result = await action();
      if (
        currentGeneration === generation.current &&
        currentToken === tokenRef.current &&
        isCurrent()
      )
        await after(result, currentGeneration);
    } catch (requestError) {
      if (
        currentGeneration === generation.current &&
        currentToken === tokenRef.current &&
        isCurrent()
      )
        setError(requestError.message);
    } finally {
      if (
        currentGeneration === generation.current &&
        currentToken === tokenRef.current &&
        isCurrent()
      )
        markPending(key, false);
      else pendingRef.current.delete(key);
    }
  };

  const upload = (event) => {
    event.preventDefault();
    if (!authorityReady) return;
    if (!files.length) return setError("Choose at least one evidence file.");
    const currentUploadGeneration = uploadGeneration.current;
    return guarded(
      "upload",
      () => multipartRequest(token, "corporate-carbon/evidence/upload", files, "files"),
      async (result, currentGeneration) => {
        setFiles([]);
        await load(currentGeneration);
        if (result?.failedCount) {
          const failures = result.data
            .filter((item) => !item.success)
            .map((item) => `${item.filename}: ${item.error?.message || item.error?.code || "Upload failed"}`)
            .join("; ");
          setError(`${result.uploadedCount} of ${result.total} files uploaded. ${failures}`);
        } else setUploadOpen(false);
      },
      () => currentUploadGeneration === uploadGeneration.current,
    );
  };

  const link = (document) => {
    if (!authorityReady) return;
    if (!entityId.trim())
      return setError("Enter the corporate-carbon entity ID.");
    const currentEntityGeneration = entityGeneration.current;
    const currentDocumentGeneration = documentGeneration.current;
    return guarded(
      `link:${document.id}`,
      () =>
        carbonRequest(token, `corporate-carbon/evidence/${document.id}/link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entityType, entityId: entityId.trim() }),
        }),
      async () => {
        if (currentEntityGeneration === entityGeneration.current)
          setEntityId("");
      },
      () =>
        currentEntityGeneration === entityGeneration.current &&
        currentDocumentGeneration === documentGeneration.current,
    );
  };

  const replace = (document) => {
    if (!authorityReady) return;
    const replacement = replacementFiles[document.id];
    if (!replacement) return setError("Choose a replacement file.");
    const revisionId = replacementRevisionIds[document.id];
    if (!revisionId || !replacementTargets[document.id]?.some((target) => target.revisionId === revisionId))
      return setError("Choose a draft revision linked to this document.");
    const currentReplacementGeneration =
      replacementGenerations.current[document.id] || 0;
    const currentDocumentGeneration = documentGeneration.current;
    return guarded(
      `replace:${document.id}`,
      () =>
        multipartRequest(
          token,
          `corporate-carbon/evidence/${document.id}/replace`,
          replacement,
          "file",
          { revisionId },
        ),
      async (_result, currentGeneration) => {
        setReplacementFiles((current) => ({ ...current, [document.id]: null }));
        await load(currentGeneration);
        setManagedDocumentId(null);
      },
      () =>
        currentReplacementGeneration ===
          (replacementGenerations.current[document.id] || 0) &&
        currentDocumentGeneration === documentGeneration.current,
    );
  };

  const loadReplacementTargets = (document) => {
    if (!authorityReady) return;
    const currentDocumentGeneration = documentGeneration.current;
    return guarded(
      `replacement-targets:${document.id}`,
      () => carbonRequest(token, `corporate-carbon/evidence/${document.id}/replacement-targets`),
      async (result) => {
        setReplacementTargets((current) => ({ ...current, [document.id]: result.data || [] }));
        setReplacementRevisionIds((current) => ({ ...current, [document.id]: "" }));
      },
      () => currentDocumentGeneration === documentGeneration.current,
    );
  };

  const archive = (document) => {
    if (!authorityReady) return;
    const currentDocumentGeneration = documentGeneration.current;
    return guarded(
      `archive:${document.id}`,
      () =>
        carbonRequest(
          token,
          `corporate-carbon/evidence/${document.id}/archive`,
          { method: "POST" },
        ),
      async (saved) => {
        documentGeneration.current += 1;
        historyGeneration.current += 1;
        setHistory([]);
        setSelectedHistoryId(null);
        setDocuments((current) =>
          current.map((row) => (row.id === saved.id ? saved : row)),
        );
        setManagedDocumentId(null);
        setArchiveTarget(null);
      },
      () => currentDocumentGeneration === documentGeneration.current,
    );
  };

  const loadHistory = async (
    document,
    targetPage,
    currentHistoryGeneration,
    currentDocumentGeneration,
  ) => {
    const result = await carbonRequest(
      token,
      `corporate-carbon/evidence/${document.id}/history?page=${targetPage}&perPage=50`,
    );
    if (
      currentHistoryGeneration === historyGeneration.current &&
      document.id === selectedHistoryIdRef.current &&
      currentDocumentGeneration === documentGeneration.current
    ) {
      setHistory((current) =>
        targetPage === 1 ? result.data : [...current, ...result.data],
      );
      setHistoryTotal(result.total);
      setHistoryPage(targetPage);
    }
  };

  const viewHistory = (document) => {
    if (!authorityReady) return;
    const currentHistoryGeneration = ++historyGeneration.current;
    const currentDocumentGeneration = documentGeneration.current;
    setSelectedHistoryId(document.id);
    selectedHistoryIdRef.current = document.id;
    setHistory([]);
    setHistoryTotal(0);
    setHistoryPage(1);
    return guarded(
      `history:${document.id}`,
      () =>
        loadHistory(
          document,
          1,
          currentHistoryGeneration,
          currentDocumentGeneration,
        ),
      async () => {},
      () =>
        currentHistoryGeneration === historyGeneration.current &&
        document.id === selectedHistoryIdRef.current &&
        currentDocumentGeneration === documentGeneration.current,
    );
  };

  const closeManage = () => {
    historyGeneration.current += 1;
    selectedHistoryIdRef.current = null;
    setSelectedHistoryId(null);
    setHistory([]);
    setHistoryTotal(0);
    setHistoryPage(1);
    setManagedDocumentId(null);
    setReplacementTargets({});
    setReplacementRevisionIds({});
  };

  const download = (document) => {
    if (!authorityReady) return;
    const currentDocumentGeneration = documentGeneration.current;
    return guarded(
      `download:${document.id}`,
      async () => {
        const response = await fetch(
          `${hostname}/corporate-carbon/evidence/${document.id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || "Evidence download failed.");
        }
        return { blob: await response.blob(), response };
      },
      async ({ blob, response }) => {
        triggerBlobDownload(blob, response, document);
      },
      () => currentDocumentGeneration === documentGeneration.current,
    );
  };

  const loadMore = () =>
    guarded(
      "load-more",
      () => load(generation.current, page + 1),
      async () => {},
    );

  const loadMoreHistory = () => {
    const document = documents.find(({ id }) => id === selectedHistoryId);
    if (!document || !authorityReady) return;
    const currentHistoryGeneration = historyGeneration.current;
    const currentDocumentGeneration = documentGeneration.current;
    const currentToken = token;
    return guarded(
      `history:load-more:${document.id}`,
      () =>
        loadHistory(
          document,
          historyPage + 1,
          currentHistoryGeneration,
          currentDocumentGeneration,
        ),
      async () => {},
      () =>
        currentToken === tokenRef.current &&
        currentHistoryGeneration === historyGeneration.current &&
        document.id === selectedHistoryIdRef.current &&
        currentDocumentGeneration === documentGeneration.current,
    );
  };

  const managedDocument = documents.find(
    ({ id }) => id === managedDocumentId,
  );
  const formatBytes = carbonFileSize;
  const formatDate = (value) =>
    value ? new Date(value).toLocaleString() : "Unknown";
  const formatType = (document) => {
    if (document.extension) return document.extension.replace(/^\./, "").toUpperCase();
    if (document.mimeType === "application/pdf") return "PDF";
    return document.mimeType || "Unknown";
  };

  return (
    <CarbonAdminPage
      title="Carbon evidence"
      description="Upload, link and review supporting documents for carbon accounting records."
      variant="list"
      actions={(
        <Button
          color="primary"
          disabled={!authorityReady || loading}
          onClick={() => {
            setError("");
            setUploadOpen(true);
          }}
          variant="contained"
        >
          Upload evidence
        </Button>
      )}
    >
        <CarbonFeedback
          loading={loading}
          loadingMessage="Loading evidence..."
          error={error}
          empty={!loading && authorityReady && documents.length === 0}
          emptyMessage="No evidence documents."
          onRetry={token && documents.length === 0 && !loading ? () => {
            const currentGeneration = generation.current;
            setError("");
            setLoading(true);
            load(currentGeneration, 1)
              .catch((requestError) => {
                if (currentGeneration === generation.current)
                  setError(requestError.message);
              })
              .finally(() => {
                if (currentGeneration === generation.current) setLoading(false);
              });
          } : undefined}
          retryLabel="Retry evidence"
        />
        <CarbonSection title="Filter documents">
          <CarbonSelectInput label="Category" name="category" choices={[{ id: 'supporting', name: 'Supporting evidence' }, { id: 'export', name: 'Export' }, { id: 'batch_import', name: 'Batch import file' }, { id: 'unclassified', name: 'Unclassified legacy file' }]} value={category} emptyText="All categories" onChange={event => setCategory(event.target.value)} />
          <Typography variant="body2">Legacy files without recorded provenance are not assumed to be generated exports. Identical filenames do not imply replacement; use replacement history in Manage.</Typography>
          <ReportingRevisionFilter token={token} value={filterRevisionId} onChange={setFilterRevisionId} inventoryId={filterInventoryId} onInventoryChange={value => { setFilterInventoryId(value); setFilterRevisionId(''); }} />
        </CarbonSection>
        {!loading && documents.length > 0 && (
          <CarbonSection
            title="Evidence documents"
            description="Supporting files uploaded for the active account, including archived records."
          >
            <CarbonDataTable label="Evidence documents">
              <TableHead>
                <TableRow>
                  <TableCell>Filename</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Linked to</TableCell>
                  <TableCell>Uploaded by</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <CarbonTableRow
                    ariaLabel={`View evidence ${document.displayFilename}`}
                    key={document.id}
                    onSelect={routeHistory ? () => routeHistory.push(carbonResourcePath("carbonEvidence", document.id)) : undefined}
                  >
                    <TableCell className={classes.filename}>{document.displayFilename}</TableCell>
                    <TableCell><CarbonStatus value={document.status} /></TableCell>
                    <TableCell>{formatType(document)}</TableCell>
                    <TableCell>{{ supporting: 'Supporting evidence', export: 'Export', batch_import: 'Batch import file', unclassified: 'Unclassified legacy file' }[document.category] || 'Not recorded'}</TableCell>
                    <TableCell>{document.linkCount === 0 ? 'Not linked' : `${document.linkCount ?? 'Unknown'} links`}</TableCell>
                    <TableCell>{document.uploadedBy || 'Not recorded'}</TableCell>
                    <TableCell>{formatBytes(document.byteSize)}</TableCell>
                    <TableCell>{formatDate(document.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Box className={classes.tableActions} justifyContent="flex-end">
                        <Button
                          aria-label={`Download ${document.displayFilename}`}
                          color="primary"
                          disabled={!authorityReady || pending.includes(`download:${document.id}`)}
                          onClick={() => download(document)}
                          size="small"
                        >
                          Download
                        </Button>
                        <Button
                          aria-label={`Manage ${document.displayFilename}`}
                          disabled={!authorityReady || pending.includes(`history:${document.id}`)}
                          onClick={(event) => {
                            event.stopPropagation();
                            setManagedDocumentId(document.id);
                          }}
                          size="small"
                        >
                          Link / Manage
                        </Button>
                      </Box>
                    </TableCell>
                  </CarbonTableRow>
                ))}
              </TableBody>
            </CarbonDataTable>
          </CarbonSection>
        )}
        {documents.length < total && (
          <Button
            disabled={!authorityReady || pending.includes("load-more")}
            onClick={loadMore}
          >
            Load more evidence
          </Button>
        )}
        <Dialog
          BackdropProps={process.env.NODE_ENV === "test" ? { transitionDuration: 0 } : undefined}
          fullWidth
          maxWidth="sm"
          onClose={() => !pending.includes("upload") && setUploadOpen(false)}
          open={uploadOpen}
          transitionDuration={process.env.NODE_ENV === "test" ? 0 : undefined}
          aria-labelledby="evidence-upload-title"
        >
          <DialogTitle id="evidence-upload-title">Upload evidence</DialogTitle>
          <CarbonForm aria-label="Evidence upload" onSubmit={upload}>
            <DialogContent className={classes.dialogContent}>
              <Typography color="textSecondary" variant="body2">
                Add up to 10 supporting documents to the active account. You can link each one to a carbon record after upload.
              </Typography>
              {error && <Typography color="error" role="alert">{error}</Typography>}
              <CarbonFileInput
                fileName={files.length ? files.map((file) => file.name).join(", ") : undefined}
                label="Evidence files"
                multiple
                name="evidenceFile"
                onChange={(event) => {
                  uploadGeneration.current += 1;
                  const selectedFiles = Array.from(event.target.files || []);
                  if (selectedFiles.length > 10) {
                    setFiles([]);
                    setError("Choose no more than 10 evidence files.");
                    event.target.value = "";
                    return;
                  }
                  setError("");
                  setFiles(selectedFiles);
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
              <Button
                color="primary"
                type="submit"
                disabled={!authorityReady || !files.length || pending.includes("upload")}
                variant="contained"
              >
                {pending.includes("upload") ? "Uploading..." : "Upload files"}
              </Button>
            </DialogActions>
          </CarbonForm>
        </Dialog>
        <Dialog
          BackdropProps={process.env.NODE_ENV === "test" ? { transitionDuration: 0 } : undefined}
          fullWidth
          maxWidth="md"
          onClose={closeManage}
          open={Boolean(managedDocument)}
          transitionDuration={process.env.NODE_ENV === "test" ? 0 : undefined}
          aria-labelledby="manage-evidence-title"
        >
          <DialogTitle id="manage-evidence-title">Manage evidence</DialogTitle>
          {managedDocument && (
            <>
              <DialogContent className={classes.dialogContent}>
                {error && <Typography color="error" role="alert">{error}</Typography>}
                <Box className={classes.metadata}>
                  <Box>
                    <Typography className={classes.metadataLabel} color="textSecondary" variant="caption">Filename</Typography>
                    <Typography className={classes.filename} variant="body2">{managedDocument.displayFilename}</Typography>
                  </Box>
                  <Box>
                    <Typography className={classes.metadataLabel} color="textSecondary" variant="caption">Status</Typography>
                    <CarbonStatus value={managedDocument.status} />
                  </Box>
                  <Box>
                    <Typography className={classes.metadataLabel} color="textSecondary" variant="caption">File details</Typography>
                    <Typography variant="body2">{formatType(managedDocument)} · {formatBytes(managedDocument.byteSize)}</Typography>
                  </Box>
                </Box>
                <Typography variant="body2" style={{ overflowWrap: 'anywhere' }}>SHA-256: {managedDocument.fileHash || 'Not recorded'}</Typography>
                {managedDocument.status === "active" && (
                  <CarbonSection
                    title="Link to a carbon record"
                    description="Associate this file with one activity, site, or inventory revision."
                  >
                    <EvidenceLinkTarget
                      entityGeneration={entityGeneration}
                      entityId={entityId}
                      entityType={entityType}
                      setEntityId={setEntityId}
                      setEntityType={setEntityType}
                    />
                    <Button
                      aria-label={`Link ${managedDocument.displayFilename}`}
                      color="primary"
                      disabled={!authorityReady || pending.includes(`link:${managedDocument.id}`)}
                      onClick={() => link(managedDocument)}
                      variant="contained"
                    >
                      Link evidence
                    </Button>
                  </CarbonSection>
                )}
                {managedDocument.status === "active" && (
                  <CarbonSection
                    title="Replace file"
                    description="The original remains usable while the replacement is scanned. After a successful scan, only links in the selected draft move. Historical and site-only links stay with the original."
                  >
                    <Button disabled={!authorityReady || pending.includes(`replacement-targets:${managedDocument.id}`)} onClick={() => loadReplacementTargets(managedDocument)} variant="outlined">Load draft targets</Button>
                    {replacementTargets[managedDocument.id] && replacementTargets[managedDocument.id].length === 0 && <Typography role="status">No linked draft revision is available. Replacement is disabled until this document is linked to a draft.</Typography>}
                    {replacementTargets[managedDocument.id]?.length > 0 && <CarbonSelectInput
                      label="Draft revision for replacement"
                      name="replacementRevisionId"
                      choices={replacementTargets[managedDocument.id].map((target) => ({ id: target.revisionId, name: `${target.reportingYear} · ${reportingRevisionStatusLabel({ ...target, status: "draft" })} · ${target.linkCount} links` }))}
                      emptyText="Select draft revision"
                      value={replacementRevisionIds[managedDocument.id] || ""}
                      onChange={(event) => setReplacementRevisionIds((current) => ({ ...current, [managedDocument.id]: event.target.value }))}
                    />}
                    <CarbonFileInput
                      fileName={replacementFiles[managedDocument.id]?.name}
                      label={`Replacement file for ${managedDocument.displayFilename}`}
                      name={`replacement-${managedDocument.id}`}
                      onChange={(event) => {
                        replacementGenerations.current[managedDocument.id] =
                          (replacementGenerations.current[managedDocument.id] || 0) + 1;
                        setReplacementFiles((current) => ({
                          ...current,
                          [managedDocument.id]: event.target.files[0] || null,
                        }));
                      }}
                    />
                    <Button
                      aria-label={`Replace ${managedDocument.displayFilename}`}
                      color="primary"
                      disabled={!authorityReady || !replacementFiles[managedDocument.id] || !replacementRevisionIds[managedDocument.id] || pending.includes(`replace:${managedDocument.id}`)}
                      onClick={() => replace(managedDocument)}
                      variant="outlined"
                    >
                      Replace file
                    </Button>
                  </CarbonSection>
                )}
                <CarbonSection
                  title="Replacement history"
                  description="Previous versions of this evidence document."
                >
                  {selectedHistoryId !== managedDocument.id ? (
                    <Button
                      aria-label={`View history ${managedDocument.displayFilename}`}
                      disabled={!authorityReady || pending.includes(`history:${managedDocument.id}`)}
                      onClick={() => viewHistory(managedDocument)}
                      variant="outlined"
                    >
                      Load history
                    </Button>
                  ) : pending.includes(`history:${managedDocument.id}`) && history.length === 0 ? (
                    <div role="status">Loading replacement history...</div>
                  ) : history.length === 0 ? (
                    <Typography color="textSecondary" variant="body2">No previous versions.</Typography>
                  ) : (
                    <CarbonDataTable label="Replacement history" className={classes.historyTable}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Filename</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Uploaded</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>{entry.displayFilename}</TableCell>
                            <TableCell><CarbonStatus value={entry.status} />{replacementScanMessage(entry) && <Typography role="alert" variant="body2">{replacementScanMessage(entry)}</Typography>}</TableCell>
                            <TableCell>{formatDate(entry.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </CarbonDataTable>
                  )}
                  {history.length < historyTotal && (
                    <Button
                      disabled={pending.includes(`history:load-more:${selectedHistoryId}`)}
                      onClick={loadMoreHistory}
                    >
                      Load more history
                    </Button>
                  )}
                </CarbonSection>
              </DialogContent>
              <DialogActions>
                {managedDocument.status === "active" && (
                  <Button
                    aria-label={`Archive ${managedDocument.displayFilename}`}
                    className={classes.danger}
                    disabled={!authorityReady || pending.includes(`archive:${managedDocument.id}`)}
                    onClick={() => setArchiveTarget(managedDocument)}
                  >
                    Archive evidence
                  </Button>
                )}
                <Box flex={1} />
                <Button onClick={closeManage}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        <CarbonConfirmDialog
          confirmLabel={archiveTarget && pending.includes(`archive:${archiveTarget.id}`) ? "Archiving…" : "Archive"}
          description="The file remains available in evidence history but will no longer be active."
          disabled={Boolean(archiveTarget && pending.includes(`archive:${archiveTarget.id}`))}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={() => archiveTarget && archive(archiveTarget)}
          open={Boolean(archiveTarget)}
          title={archiveTarget ? `Archive ${archiveTarget.displayFilename}?` : "Archive evidence?"}
        />
    </CarbonAdminPage>
  );
};

const evidenceType = (document) => {
  if (document?.extension) return document.extension.replace(/^\./, "").toUpperCase();
  if (document?.mimeType === "application/pdf") return "PDF";
  return document?.mimeType || "Unknown";
};
const evidenceSize = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return "Unknown";
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const CarbonEvidenceShow = ({ accessToken, id }) => {
  const tokenOps = useTokenOps();
  const token = accessToken || tokenOps.account?.accessToken;
  const [document, setDocument] = useState(null);
  const [replacementHistory, setReplacementHistory] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    const findDocument = async () => {
      let page = 1;
      while (active) {
        const result = await carbonRequest(token, `corporate-carbon/evidence?page=${page}&perPage=50&includeArchived=true`);
        const match = result.data.find((item) => item.id === id);
        if (match) return match;
        if (page * 50 >= result.total) return null;
        page += 1;
      }
      return null;
    };
    Promise.all([
      findDocument(),
      carbonRequest(token, `corporate-carbon/evidence/${id}/history?page=1&perPage=50`),
    ]).then(([item, historyResult]) => {
      if (active) {
        setDocument(item);
        setReplacementHistory(historyResult.data || []);
      }
    }).catch((requestError) => {
      if (active) setError(friendlyCarbonError(requestError));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, token]);
  return <CarbonAdminPage title="Evidence document" description="Supporting document metadata and replacement history.">
    {loading ? <div role="status">Loading evidence…</div> : null}
    {error ? <Typography color="error" role="alert">{error}</Typography> : null}
    {!loading && !error && !document ? <Typography role="alert">Evidence document not found.</Typography> : null}
    {document ? <>
      <CarbonSection title={document.displayFilename}><dl><dt>Status</dt><dd><CarbonStatus value={document.status} /></dd><dt>Type</dt><dd>{evidenceType(document)}</dd><dt>Size</dt><dd>{evidenceSize(document.byteSize)}</dd><dt>Uploaded</dt><dd>{document.createdAt ? new Date(document.createdAt).toLocaleString() : "Unknown"}</dd></dl></CarbonSection>
      <CarbonSection title="Replacement history">{replacementHistory.length ? <CarbonDataTable label="Replacement history"><TableHead><TableRow><TableCell>Filename</TableCell><TableCell>Status</TableCell><TableCell>Uploaded</TableCell></TableRow></TableHead><TableBody>{replacementHistory.map((entry) => <TableRow key={entry.id}><TableCell>{entry.displayFilename}</TableCell><TableCell><CarbonStatus value={entry.status} /></TableCell><TableCell>{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "Unknown"}</TableCell></TableRow>)}</TableBody></CarbonDataTable> : <Typography>No previous versions.</Typography>}</CarbonSection>
    </> : null}
  </CarbonAdminPage>;
};

const carbonEvidenceResource = () => (
  <Resource
    name="carbonEvidence"
    options={{ label: "Evidence", menuGroup: "Carbon Accounting" }}
    list={CarbonEvidencePage}
    show={CarbonEvidenceShow}
  />
);

export default carbonEvidenceResource;
